import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Tooltip from '@material-ui/core/Tooltip';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Editor, { TABLE_CELL_COMPONENT, FORM_FIELD_COMPONENT } from 'tuc-webui-base/components/editors/Editor';
import './StateEditor.scss';

const ValueType = PropTypes.oneOfType([
  PropTypes.number,
  PropTypes.string,
  PropTypes.shape({}),
  PropTypes.bool,
]);

const STATE_TABLE_MODE = 0;
const STATE_FILTER_MODE = 1;
const STATE_FORM_MODE = 2;

// TableCell
const makeTableCellComponents = ({
  getState,
  memoize,
}) => {
  const StateTableCell = (props) => {
    const state = getState(props.value, props.object, props, STATE_TABLE_MODE);
    if (!state) {
      return (null);
    }
    const { tooltip, renderTooltip, className, icon: Icon, tableText } = state;
    if (!Icon && !tableText) {
      return (null);
    }
    const content = tableText ? (
      <div className={className}>
        {Icon && <Icon className="grb-left-icon" />}
        {tableText}
      </div>
    ) : (
      <div className={className}>
        {Icon && <Icon />}
      </div>
    );
    if (renderTooltip) {
      return renderTooltip(props.object, content);
    }
    if (tooltip) {
      return (
        <Tooltip title={tooltip}>
          {content}
        </Tooltip>
      );
    }
    return content;
  };

  StateTableCell.propTypes = {
    value: ValueType,
  };

  StateTableCell.defaultProps = {
    value: undefined,
  };

  if (memoize) {
    const MemoizedTableCell = React.memo(StateTableCell, (p, n) => {
      return getState(p.value, p.object, p) === getState(n.value, n.object, n);
    });
    return [MemoizedTableCell, MemoizedTableCell];
  }
  return [StateTableCell, StateTableCell];
};

// Form
const makeFormComponents = ({
  getState,
  checkedOn,
}) => {
  if (checkedOn) {
    // Checkbox form editor
    const CheckFormField = ({ object, label, error, readOnly, className, ...rest }) => {
      const state = getState(rest.value, object, rest, STATE_FORM_MODE);
      const { text = label, checked } = state || {};
      if (text) {
        return (
          <FormControlLabel
            control={(
              <Checkbox
                checked={checked}
                color="primary"
              />
            )}
            label={text}
            className={classnames('grb-state-checkbox', className, state.className)}
          />
        );
      }
      return (
        <Checkbox
          checked={checked}
          color="primary"
          className={classnames('grb-state-checkbox', className, state.className)}
        />
      );
    };

    CheckFormField.propTypes = {
      value: ValueType,
      label: PropTypes.string,
      error: PropTypes.string,
      readOnly: PropTypes.bool,
      className: PropTypes.string,
      object: PropTypes.shape({}),
    };

    CheckFormField.defaultProps = {
      value: undefined,
      label: '',
      error: undefined,
      readOnly: false,
      className: undefined,
      object: undefined,
    };

    return [CheckFormField];
  }

  const StateTextField = ({ object, label, error, property, readOnly, className, ...rest }) => {
    const {
      text,
      className: stateClassname,
    } = getState(rest.value, object, rest, STATE_FORM_MODE) || {};

    const margin = property.margin || 'normal';
    return (
      <TextField
        error={!!error}
        label={label}
        className={classnames('grb-state-textfield', readOnly && 'grb-readonly', className, stateClassname)}
        value={!text ? ' ' : text}
        margin={margin}
        InputProps={{
          readOnly: Boolean(readOnly === undefined ? false : readOnly),
          // startAdornment: Icon ? (
          //   <InputAdornment position="start">
          //     <Icon />
          //   </InputAdornment>
          // ) : undefined,
        }}
        helperText={error || (property ? property.description : undefined)}
      />
    );
  };

  StateTextField.propTypes = {
    value: ValueType,
    label: PropTypes.string,
    error: PropTypes.string,
    readOnly: PropTypes.bool,
    property: PropTypes.shape({
      description: PropTypes.string,
      margin: PropTypes.string,
    }),
    className: PropTypes.string,
    object: PropTypes.shape({}),
  };

  StateTextField.defaultProps = {
    value: undefined,
    label: '',
    error: undefined,
    property: {},
    readOnly: false,
    className: undefined,
    object: undefined,
  };

  return [StateTextField];
};

const computeEditorOptions = (options) => {
  const {
    states,
    getTableText,
    getIcon,
    getText,
    getFilterText,
    getStateValue,
    getTooltip,
    getClassname,
    checkedOn,
  } = options;
  const { memoize = !!states } = options;

  const computeState = options.getState || ((v) => {
    return states && states.find((s) => {
      return s.values ? (s.values.indexOf(v) >= 0) : (s.value === v);
    });
  });

  const isChecked = checkedOn
    ? (Array.isArray(checkedOn) && (v => (checkedOn.indexOf(v) >= 0)) || (v => (checkedOn === v)))
    : undefined;

  const getState = ((value, object, props, mode) => {
    const v = getStateValue ? getStateValue(value, object, props, mode) : value;
    const state = computeState(v, object, props, mode) || {};
    if (getTableText && (mode !== STATE_FORM_MODE)) {
      state.tableText = getTableText(v, object, props, mode);
    }
    if (mode === STATE_FILTER_MODE) {
      state.filterText = getFilterText
        ? getFilterText(v, object, props, mode)
        : (state.filterText || state.tableText || v);
    } else {
      if (mode === STATE_FORM_MODE) {
        state.text = getText ? getText(v, object, props, mode) : state.text;
        if (isChecked) {
          state.checked = isChecked(v);
        }
      }
      if (getTooltip) {
        state.tooltip = getTooltip(v, object, props, mode);
      }
      if (getIcon) {
        state.icon = getIcon(v, object, props, mode);
      }
      if (getClassname) {
        state.className = getClassname(v, object, props, mode);
      }
    }
    return state;
  });
  const stateOptions = {
    ...options,
    getState,
    memoize,
  };
  return {
    ...options,
    getState,
    [TABLE_CELL_COMPONENT]: makeTableCellComponents(stateOptions),
    [FORM_FIELD_COMPONENT]: makeFormComponents(stateOptions),
  };
};

class StateEditor extends Editor {
  constructor(name, options) {
    super(name || 'state', computeEditorOptions(options));
  }

  formatValue(value, object, propName, props) {
    const { getState } = this.components;
    const { filterText } = getState(value, object, props, STATE_FILTER_MODE);
    return filterText;
  }

  getSortValue(value, fvalue) {
    return fvalue;
  }
}

export { StateEditor as default, STATE_FILTER_MODE, STATE_TABLE_MODE, STATE_FORM_MODE };
